/**
 * InsurerListPage — liste minimale des assureurs (9.V.2b).
 * Table nom / code / statut, recherche, lien vers la fiche. États dédiés
 * loading / error / empty. Lecture seule (CRUD structurel assureur = 9.V.1).
 */

import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { getApiErrorMessage, normalizeLang } from "../../lib/apiErrorMessage";
import { useInsurers } from "../../lib/queries/insurers";

export function InsurerListPage() {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const lang = normalizeLang(language);
  const [q, setQ] = useState("");
  const { data, isLoading, isError, error } = useInsurers({
    q: q || undefined,
    status: "all",
    limit: 100,
  });
  const items = data?.items ?? [];

  return (
    <div className="mx-auto max-w-5xl px-4 py-6">
      <header className="mb-6">
        <h1 className="text-3xl font-semibold tracking-tight text-slate-900">
          {t("insurers.title")}
        </h1>
        <p className="mt-1 text-sm text-slate-600">{t("insurers.subtitle")}</p>
      </header>

      <label className="mb-4 block max-w-sm">
        <span className="sr-only">{t("insurers.search_placeholder")}</span>
        <input
          type="search"
          className="input"
          spellCheck={false}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={t("insurers.search_placeholder")}
        />
      </label>

      {isLoading ? (
        <p className="py-8 text-sm text-slate-500">{t("app.loading")}</p>
      ) : isError ? (
        <p role="alert" className="py-8 text-sm text-red-700">
          {getApiErrorMessage(error, lang) || t("errors.generic")}
        </p>
      ) : items.length === 0 ? (
        <p className="py-8 text-sm text-slate-500">{t("insurers.empty")}</p>
      ) : (
        <div className="card overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-200 text-sm">
            <thead>
              <tr className="text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                <th className="px-4 py-3">{t("insurers.columns.name")}</th>
                <th className="px-4 py-3">{t("insurers.columns.code")}</th>
                <th className="px-4 py-3">{t("insurers.columns.status")}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {items.map((ins) => (
                <tr key={ins.id} className="hover:bg-slate-50">
                  <td className="px-4 py-3">
                    <Link
                      to={`/insurers/${ins.id}`}
                      className="text-brand-700 focus-visible:ring-brand-500 rounded font-medium hover:underline focus-visible:outline-none focus-visible:ring-2"
                    >
                      {ins.name}
                    </Link>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-slate-600">{ins.code}</td>
                  <td className="px-4 py-3">
                    <span
                      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${
                        ins.status === "active"
                          ? "bg-emerald-50 text-emerald-700 ring-emerald-200"
                          : "bg-slate-100 text-slate-600 ring-slate-200"
                      }`}
                    >
                      {t(`insurers.status.${ins.status}`)}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
